import { useCallback, useEffect, useState } from 'react';

import { computeWaveColor } from './WaveEffect.utils';

import styles from './WaveEffect.module.scss';

type WaveEffectProps = {
    /**
     * The CSS property of the target element to compute the color from.
     */
    colorProp?: 'background-color' | 'border-color' | 'color';
};

const TRANSITION_CLASSES = [styles.appear, styles.active];

const addWaveEffect = (
    triggerRef: HTMLElement | null,
    waveRef: HTMLDivElement | null,
    colorProp?: WaveEffectProps['colorProp']
) => {
    if (!triggerRef || !waveRef) return () => {};
    const targetOriginalPosition = triggerRef.style.position;
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleTransitionEnd = () => {
        waveRef.classList.remove(...TRANSITION_CLASSES);
        triggerRef.style.position = targetOriginalPosition;
    };

    const handleClick = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            const compStyles = getComputedStyle(triggerRef);
            const borderWidth = compStyles.getPropertyValue('border-width');
            const targetPositionProperty = compStyles.getPropertyValue('position');

            // ensure the position of the wave is the same as the target on the screen
            if (targetPositionProperty === 'static') {
                triggerRef.style.position = 'relative';
            }

            // apply the transition classes to animate the wave
            waveRef.classList.remove(...TRANSITION_CLASSES);
            waveRef.classList.add(...TRANSITION_CLASSES);

            // ensure the wave has the same size and shape as the target
            waveRef.style.color = computeWaveColor(triggerRef, colorProp);
            waveRef.style.width = compStyles.getPropertyValue('width');
            waveRef.style.height = compStyles.getPropertyValue('height');
            waveRef.style.left = `-${borderWidth}`;
            waveRef.style.top = `-${borderWidth}`;
            waveRef.style.borderRadius = compStyles.getPropertyValue('border-radius');
        }, 50);
    };

    triggerRef.addEventListener('click', handleClick);
    waveRef.addEventListener('transitionend', handleTransitionEnd);
    waveRef.addEventListener('transitioncancel', handleTransitionEnd);

    const removeWaveEffect = () => {
        triggerRef.removeEventListener('click', handleClick);
        waveRef.removeEventListener('transitionend', handleTransitionEnd);
        waveRef.removeEventListener('transitioncancel', handleTransitionEnd);
        triggerRef.style.position = targetOriginalPosition;
        clearTimeout(timeoutId);
    };

    return removeWaveEffect;
};

const useWaveEffect = (options: WaveEffectProps = {}) => {
    const { colorProp } = options;
    const [waveTriggerEl, setWaveTriggerEl] = useState<HTMLElement | null>(null);
    const [waveEffectEl, setWaveEffectEl] = useState<HTMLDivElement | null>(null);

    const waveTriggerRef = useCallback((element: HTMLElement | null) => {
        setWaveTriggerEl(element);
    }, []);

    const waveEffectRef = useCallback((element: HTMLDivElement | null) => {
        setWaveEffectEl(element);
    }, []);

    useEffect(() => {
        const removeWaveEffect = addWaveEffect(waveTriggerEl, waveEffectEl, colorProp);
        return () => {
            removeWaveEffect();
        };
    }, [waveTriggerEl, waveEffectEl, colorProp]);

    return { waveTriggerRef, waveEffectRef };
};

export { useWaveEffect };
